import { createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Open/closed state for the assistant widget.
 *
 * This lives in a provider rather than inside the widget because two separate
 * places need it: the widget renders the panel, and the invitation band on the
 * home page renders a button that must open it. Props cannot cross the router
 * Outlet boundary, so context is the smallest thing that works.
 *
 * The conversation itself is NOT stored here -- it lives in useAssistantChat,
 * called by the always-mounted widget, so the transcript survives the panel
 * closing and reopening.
 */
const AssistantContext = createContext(null);

export const AssistantProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Stable references: the band re-renders on every route change, and a new
  // function identity each time would defeat memoisation downstream.
  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo(() => ({ isOpen, open, close }), [isOpen, open, close]);

  return (
    <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
  );
};

/**
 * Returns the assistant open state and controls.
 *
 * Fails loudly rather than returning a silent no-op: a button that does
 * nothing is exactly the bug this widget exists to avoid.
 */
export const useAssistantControls = () => {
  const value = useContext(AssistantContext);

  if (!value) {
    throw new Error(
      'useAssistantControls must be used inside an AssistantProvider'
    );
  }

  return value;
};

export default AssistantProvider;
